import { useEffect } from 'react';

/**
 * Custom hook that hides the mouse cursor after a period of inactivity.
 *
 * Sets `document.body.style.cursor` to 'none' after the specified delay.
 * On any mouse movement, restores the cursor to 'default' and restarts the timer.
 * Cleans up event listeners and timeouts on unmount, restoring cursor to 'default'.
 *
 * @param delayMs - Milliseconds of inactivity before hiding the cursor (default 3000)
 */
export function useCursorHide(delayMs: number = 3000): void {
  useEffect(() => {
    let timeout: number;

    const hide = () => {
      document.body.style.cursor = 'none';
    };

    const show = () => {
      document.body.style.cursor = 'default';
      clearTimeout(timeout);
      timeout = window.setTimeout(hide, delayMs);
    };

    document.addEventListener('mousemove', show);
    timeout = window.setTimeout(hide, delayMs);

    return () => {
      document.removeEventListener('mousemove', show);
      clearTimeout(timeout);
      document.body.style.cursor = 'default';
    };
  }, [delayMs]);
}
